import { lazy, LazyExoticComponent, Suspense, useMemo } from 'react';

import Aadhaar from 'inquiry/components/Integration/Aadhaar';
import type FinnishTrustNetwork from 'inquiry/components/Integration/FinnishTrustNetwork';
import type Kica from 'inquiry/components/Integration/Kica';
import type MxBank from 'inquiry/components/Integration/MxBank';
import type OneId from 'inquiry/components/Integration/OneId';
import { TransitionParams } from 'inquiry/lib/types';
import {
  StepIntegrationConfig,
  StepIntegrationConfigAadhaar,
  StepIntegrationConfigFinnishTrustNetwork,
  StepIntegrationConfigKica,
  StepIntegrationConfigMxBank,
  StepIntegrationConfigOneId,
} from 'lib/interfaces/StepsConfig';
import { FullScreenLoadingSpinner } from 'verify/components/LoadingSpinner';

interface InquiryIntegrationViewProps {
  config: StepIntegrationConfig;
  handleContinue: (transitionParams?: TransitionParams) => void;
  previewMode?: boolean;
}

interface MxBankStruct {
  type: 'mx_bank';
  component: LazyExoticComponent<typeof MxBank>;
  integrationConfig: StepIntegrationConfigMxBank;
}

interface KicaStruct {
  type: 'kica';
  component: LazyExoticComponent<typeof Kica>;
  integrationConfig: StepIntegrationConfigKica;
}

interface AadhaarStruct {
  type: 'aadhaar';
  component: LazyExoticComponent<typeof Aadhaar>;
  integrationConfig: StepIntegrationConfigAadhaar;
}

interface FinnishTrustNetworkStruct {
  type: 'finnish_trust_network';
  component: LazyExoticComponent<typeof FinnishTrustNetwork>;
  integrationConfig: StepIntegrationConfigFinnishTrustNetwork;
}

interface OneIdStruct {
  type: 'one_id';
  component: LazyExoticComponent<typeof OneId>;
  integrationConfig: StepIntegrationConfigOneId;
}

type IntegrationStruct =
  | MxBankStruct
  | KicaStruct
  | AadhaarStruct
  | FinnishTrustNetworkStruct
  | OneIdStruct;

const InquiryIntegrationView = ({
  config,
  handleContinue,
  previewMode = false,
}: InquiryIntegrationViewProps) => {
  // make sure we only lazy load the one we want, and that we only load it once
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const externalIntegrationStruct = useMemo<IntegrationStruct>(() => {
    const type = config.type;
    switch (type) {
      case 'mx_bank':
        return {
          type,
          component: lazy(() => import('inquiry/components/Integration/MxBank')),
          integrationConfig: config,
        };
      case 'kica':
        return {
          type,
          component: lazy(() => import('inquiry/components/Integration/Kica')),
          integrationConfig: config,
        };
      case 'aadhaar':
        return {
          type,
          component: lazy(() => import('inquiry/components/Integration/Aadhaar')),
          integrationConfig: config,
        };
      case 'finnish_trust_network':
        return {
          type,
          component: lazy(() => import('inquiry/components/Integration/FinnishTrustNetwork')),
          integrationConfig: config,
        };
      case 'one_id':
        return {
          type,
          component: lazy(() => import('inquiry/components/Integration/OneId')),
          integrationConfig: config,
        };
      default:
        return {
          type,
          component: null,
          integrationConfig: config,
        };
    }
  }, [config]);

  if (previewMode) {
    switch (externalIntegrationStruct.type) {
      case 'mx_bank':
        // TODO: [PER-20145] Replace with nice MX preview asset.
        return (
          <>
            <h1>MX Bank Integration</h1>
            <p>
              This is a placeholder for the MX Bank step. The real widget cannot load in FLE
              previews.
            </p>
          </>
        );
      case 'kica':
      case 'aadhaar':
      case 'finnish_trust_network':
      case 'one_id': {
        const ExternalIntegration = externalIntegrationStruct.component;
        return <ExternalIntegration flowUrl='' onComplete={() => {}} />;
      }
      default:
        return null;
    }
  }

  const { type, component: ExternalIntegration, integrationConfig } = externalIntegrationStruct;
  switch (type) {
    case 'mx_bank':
      return (
        <Suspense fallback={<FullScreenLoadingSpinner />}>
          <ExternalIntegration
            widgetUrl={integrationConfig['widget-url']}
            // TODO: [PER-20145] make this field name configurable
            memberConnected={(memberGuid) => handleContinue({ 'mx-bank-member-guid': memberGuid })}
            microdepositCreated={(microdepositGuid) =>
              handleContinue({ 'mx-bank-microdeposit-guid': microdepositGuid })
            }
            microdepositVerificationSubmitted={handleContinue}
          />
        </Suspense>
      );
    case 'kica':
      return (
        <Suspense fallback={<FullScreenLoadingSpinner />}>
          <ExternalIntegration
            flowUrl={integrationConfig['flow-url']}
            onComplete={handleContinue}
          />
        </Suspense>
      );
    case 'aadhaar':
      return (
        <Suspense fallback={<FullScreenLoadingSpinner />}>
          <ExternalIntegration
            flowUrl={integrationConfig['flow-url']}
            onComplete={handleContinue}
          />
        </Suspense>
      );
    case 'finnish_trust_network':
      return (
        <Suspense fallback={<FullScreenLoadingSpinner />}>
          <ExternalIntegration
            flowUrl={integrationConfig['flow-url']}
            onComplete={handleContinue}
          />
        </Suspense>
      );
    case 'one_id':
      return (
        <Suspense fallback={<FullScreenLoadingSpinner />}>
          <ExternalIntegration
            flowUrl={integrationConfig['flow-url']}
            onComplete={handleContinue}
          />
        </Suspense>
      );
    default:
      // this should never happen
      return <p>Integration type {type} not found.</p>;
  }
};

export default InquiryIntegrationView;
